import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import Modal, { Field, TextInput, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/lib/format";

const initial = {
  customer_id: "", unit_id: "", schedule_id: "",
  amount: "", paid_on: "", reference: "", method: "bank_transfer", notes: "",
};

export default function Payments() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);

  const load = async () => {
    const [p, c, u, s] = await Promise.all([
      api.get("/company/payments"),
      api.get("/company/customers"),
      api.get("/company/units"),
      api.get("/company/schedules"),
    ]);
    setItems(p.data);
    setCustomers(c.data);
    setUnits(u.data);
    setSchedules(s.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/payments", {
        ...form,
        amount: Number(form.amount),
        schedule_id: form.schedule_id || null,
      });
      toast.success("Payment recorded");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-payments-page">
      <PageHeader
        eyebrow="Finance"
        title="Payment references"
        description="Payments received from customers, optionally linked to a scheduled milestone."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-payment-button">+ Record Payment</PrimaryButton>}
      />

      <DataTable
        testId="payments-table"
        columns={[
          { key: "paid_on", label: "Paid On", render: (r) => fmtDate(r.paid_on) },
          { key: "customer", label: "Customer", render: (r) => <div className="font-bold">{r.customer?.name || "—"}</div> },
          { key: "unit", label: "Unit", render: (r) => <span className="font-mono">{r.unit?.unit_no || "—"}</span> },
          { key: "amount", label: "Amount", render: (r) => <span className="font-mono">{fmtCurrency(r.amount)}</span> },
          { key: "method", label: "Method" },
          { key: "reference", label: "Ref #", render: (r) => <span className="font-mono text-xs">{r.reference || "—"}</span> },
        ]}
        rows={items}
        empty="No payments recorded yet."
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Record customer payment">
        <form onSubmit={submit}>
          <Field label="Customer">
            <Select testId="form-pay-customer" required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Unit">
            <Select testId="form-pay-unit" required value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}>
              <option value="">Select unit…</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.project?.name} · {u.unit_no}</option>)}
            </Select>
          </Field>
          <Field label="Against schedule (optional)">
            <Select testId="form-pay-schedule" value={form.schedule_id} onChange={(e) => setForm({ ...form, schedule_id: e.target.value })}>
              <option value="">— Not linked —</option>
              {schedules.filter((s) => s.status !== "paid").map((s) => (
                <option key={s.id} value={s.id}>{s.milestone} · {fmtCurrency(s.amount)} · due {fmtDate(s.due_date)}</option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><TextInput testId="form-pay-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Paid on"><TextInput testId="form-pay-date" type="date" required value={form.paid_on} onChange={(e) => setForm({ ...form, paid_on: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Method">
              <Select testId="form-pay-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                <option value="bank_transfer">Bank transfer</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI / Wallet</option>
              </Select>
            </Field>
            <Field label="Reference"><TextInput testId="form-pay-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><TextInput testId="form-pay-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-company-payment">Save</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
